import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import AppContainer from '../AppContainer';
import AppHeader from '../AppHeader';
import { user } from '@web/services/api';
import { useHistory, useParams } from 'react-router-dom';
import Loading from '../common/Loading';
import DownloadAppStore from './icons/download-app-store.svg';
import DownloadGooglePlay from './icons/download-google-play.svg';
import { media } from '@web/styles/theme';
import { LeftOutlined } from '@ant-design/icons';
import { Button } from 'antd';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(100% - 72px);
  margin: auto;
  padding: 1rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  box-shadow: 0px 0px 15px rgba(0, 41, 64, 0.3);
  padding: 6rem 12rem;
  border-radius: 24px;
  margin-bottom: 72px;

  ${media.nmd} {
    padding: 2rem 4rem;
  }

  ${media.nlg} {
    padding: 3rem 6rem;
  }

  ${media.nxxl} {
    padding: 4rem 8rem;
  }
`;

const Header = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue};
  margin-bottom: 2rem;
  text-align: center;

  ${media.nlg} {
    font-size: 2rem;
  }
`;

const Description = styled.div`
  font-size: 1.5rem;
  margin-bottom: 20px;
  text-align: center;

  ${media.nlg} {
    font-size: 1rem;
  }
`;

const LinksContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  ${media.nmd} {
    grid-template-columns: 1fr;
  }
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: white;
  width: 180px;

  ${media.nlg} {
    width: 140px;
  }

  > img {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const TryInApp = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user_activity_id: userActivityId } = useParams();

  const loadActivity = useCallback(() => {
    setLoading(true);
    user
      .getUserActivity(userActivityId)
      .then(({ data: { data: activity } }) => {
        setActivity(activity.activity);
        setLoading(false);
      })
      .catch(e => {
        setActivity(null);
        setLoading(false);
      });
  }, [userActivityId]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const handleBack = useCallback(() => {
    const canGoBack = window.history.length > 1;
    if (canGoBack) {
      history.goBack();
    } else {
      history.push('/frontline');
    }
  }, [history]);

  return (
    <>
      <AppHeader title={t('idvoters.header')} />
      <AppContainer>
        <div className="p-3">
          <Button onClick={handleBack} type="default">
            <LeftOutlined />
            {t('common.back')}
          </Button>
        </div>
        <Wrapper>
          {loading ? (
            <Loading />
          ) : (
            <Content>
              <Header>{activity?.title}</Header>
              <Description>
                This action is only available on the Impactive mobile app.
                <br />
                <br />
                Download it now for iPhone or Android:
              </Description>

              <LinksContainer>
                <StyledLink
                  target="_blank"
                  href="https://apps.apple.com/us/app/impactive/id1511162222"
                >
                  <img src={DownloadAppStore} alt="Download on the App Store" />
                </StyledLink>
                <StyledLink
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=org.codeforcommunity.Outvote&hl=en_US"
                >
                  <img src={DownloadGooglePlay} alt="Download on the Google Play" />
                </StyledLink>
              </LinksContainer>
            </Content>
          )}
        </Wrapper>
      </AppContainer>
    </>
  );
};

export default TryInApp;
